/**
 * ROTEIRO DE ENTREVISTA — o guia que MAPEIA a entrevista inteira.
 *
 * Cada entrevista deixa de ser uma conversa solta com uma observação no fim:
 * aqui está o caminho completo, na ordem em que a conversa acontece — abertura,
 * trajetória, técnico da função, comportamento (situações reais), fit com a
 * cultura, segurança do trabalho, disponibilidade, remuneração e encerramento —
 * com o que observar em cada resposta e uma nota de 1 a 5 por pergunta.
 *
 * O entrevistador anota DENTRO do roteiro (aba Entrevistas → botão do roteiro),
 * fecha com um parecer e o registro fica no candidato. Duas entrevistas para a
 * mesma vaga passam a ser comparáveis, porque as duas percorreram os mesmos
 * pontos.
 *
 * Fica em src/ (e não em server/) pelo mesmo motivo de src/avaliacao-modelos.ts
 * e src/clima-modelo.ts: a tela monta o roteiro em branco a partir daqui, e o
 * roteiro APLICADO é gravado inteiro (perguntas + respostas) — assim, mudar o
 * catálogo depois não reescreve entrevistas que já aconteceram.
 */

export interface PerguntaRoteiro {
  /** Estável dentro do roteiro: "<bloco>.<n>" (ex.: "tecnico.3"). */
  id: string;
  texto: string;
  /** O que observar na resposta — aparece em cinza embaixo da pergunta. */
  dica?: string;
  /** O que está sendo avaliado ali (vira a etiqueta da pergunta). */
  competencia?: string;
}

export interface BlocoRoteiro {
  id: string;
  titulo: string;
  /** Para que serve este trecho da conversa. */
  objetivo: string;
  /** Tempo sugerido — o roteiro também é o plano de tempo da entrevista. */
  minutos: number;
  perguntas: PerguntaRoteiro[];
  /** Bloco acrescentado na hora (IA ou à mão) — não veio do catálogo. */
  extra?: boolean;
}

export interface ModeloRoteiro {
  key: string;
  nome: string;
  descricao: string;
  blocos: BlocoRoteiro[];
}

/* ------------------------------------------------------------------ *
 *  Escala e vocabulário                                               *
 * ------------------------------------------------------------------ */

/**
 * Nota por pergunta. Cinco pontos, com rótulo à vista: sem o rótulo, cada
 * entrevistador inventa a própria régua e a comparação entre candidatos perde
 * o sentido.
 */
export const ESCALA_ROTEIRO = [
  { valor: 1, rotulo: "Não atende", descricao: "Resposta não cobre o que a vaga exige." },
  { valor: 2, rotulo: "Atende em parte", descricao: "Tem alguma base, mas com lacuna clara." },
  { valor: 3, rotulo: "Atende", descricao: "Faz o que a função pede, no nível esperado." },
  { valor: 4, rotulo: "Atende bem", descricao: "Domina e traz exemplos concretos." },
  { valor: 5, rotulo: "Supera", descricao: "Referência: resolve além do esperado." },
] as const;

/** Recomendações do parecer — e o que cada uma faz com o status da entrevista. */
export const RECOMENDACOES = [
  // "Aprovado" seco vem PRIMEIRO: era o que faltava. A lista só tinha
  // "Aprovar com ressalva" e "Seguir para a próxima etapa" — quem queria dizer
  // simplesmente que a pessoa foi aprovada não achava a opção, e acabava
  // marcando "com ressalva" em quem não tinha ressalva nenhuma.
  { valor: "Aprovado", status: "Aprovado" },
  { valor: "Aprovar com ressalva", status: "Aprovado" },
  { valor: "Seguir para a próxima etapa", status: "Aprovado" },
  { valor: "Banco de talentos", status: "Realizada" },
  { valor: "Não seguir", status: "Reprovado" },
  { valor: "Aguardando decisão", status: "Realizada" },
] as const;

export type Recomendacao = (typeof RECOMENDACOES)[number]["valor"];

/** Como a entrevista foi feita (entra no cabeçalho do roteiro impresso). */
export const MODALIDADES = ["Presencial", "Vídeo", "Telefone"] as const;

/**
 * CHECKLIST DE CONDUÇÃO — o que não pode faltar antes, durante e depois.
 * É a parte do mapeamento que não é pergunta: preparo, combinados e o
 * fechamento do processo (retorno ao candidato, documentos anexados).
 */
export interface ItemChecklist {
  id: string;
  texto: string;
  momento: "Antes" | "Durante" | "Depois";
}

export const CHECKLIST_CONDUCAO: ItemChecklist[] = [
  { id: "c1", momento: "Antes", texto: "Li o currículo e a triagem do candidato" },
  { id: "c2", momento: "Antes", texto: "Tenho em mãos a descrição da vaga (atividades, requisitos, salário)" },
  { id: "c3", momento: "Antes", texto: "Confirmei horário, local/link e duração com o candidato" },
  { id: "c4", momento: "Durante", texto: "Me apresentei e expliquei as etapas do processo" },
  { id: "c5", momento: "Durante", texto: "Falei da SERCAMP, da área e do que a vaga faz no dia a dia" },
  { id: "c6", momento: "Durante", texto: "Confirmei dados de contato e endereço" },
  { id: "c7", momento: "Durante", texto: "Conferi documentos e certificados (RG/CPF, CNH, NRs)" },
  { id: "c8", momento: "Durante", texto: "Abri espaço para as perguntas do candidato" },
  { id: "c9", momento: "Durante", texto: "Combinei o próximo passo e o prazo de retorno" },
  { id: "c10", momento: "Depois", texto: "Anexei currículo e documentos na aba Entrevistas" },
  { id: "c11", momento: "Depois", texto: "Registrei o parecer e a nota neste roteiro" },
  { id: "c12", momento: "Depois", texto: "Dei retorno ao candidato (aprovado ou não)" },
];

/* ------------------------------------------------------------------ *
 *  Montagem dos blocos                                                *
 * ------------------------------------------------------------------ */

type PerguntaBase = Omit<PerguntaRoteiro, "id">;

function montarBloco(
  id: string,
  titulo: string,
  objetivo: string,
  minutos: number,
  perguntas: PerguntaBase[],
): BlocoRoteiro {
  return {
    id,
    titulo,
    objetivo,
    minutos,
    perguntas: perguntas.map((p, i) => ({ ...p, id: `${id}.${i + 1}` })),
  };
}

/* ---------- Blocos comuns a todos os roteiros ---------- */

const ABERTURA = (): BlocoRoteiro =>
  montarBloco(
    "abertura",
    "1. Abertura e apresentação",
    "Deixar o candidato à vontade, explicar como será a conversa e ouvir a apresentação dele.",
    5,
    [
      {
        texto: "Apresente-se: conte um pouco sobre você e sua trajetória até aqui.",
        dica: "Clareza e organização da fala; se o que conta bate com o currículo.",
        competencia: "Comunicação",
      },
      {
        texto: "O que você já sabe sobre a SERCAMP e o que chamou sua atenção nesta vaga?",
        dica: "Se pesquisou a empresa ou está mandando currículo para tudo.",
        competencia: "Interesse pela vaga",
      },
      {
        texto: "Como ficou sabendo da vaga?",
        dica: "Se foi indicação, anote quem indicou — vale confirmar com a pessoa.",
        competencia: "Origem do candidato",
      },
    ],
  );

const TRAJETORIA = (): BlocoRoteiro =>
  montarBloco(
    "trajetoria",
    "2. Trajetória profissional",
    "Entender o que a pessoa realmente fazia, com quem, por quanto tempo e por que saiu.",
    10,
    [
      {
        texto: "Fale da sua última experiência: empresa, função, tempo de casa e o que fazia no dia a dia.",
        dica: "Peça o dia a dia concreto, não o cargo. Confira se bate com o currículo.",
        competencia: "Experiência",
      },
      {
        texto: "Quais eram suas principais responsabilidades e a quem você se reportava?",
        dica: "Tamanho da equipe, nível de autonomia e a quem prestava contas.",
        competencia: "Autonomia",
      },
      {
        texto: "Por que saiu (ou por que quer sair) da última empresa?",
        dica: "Atenção a crítica excessiva ao antigo empregador e a versões que mudam.",
        competencia: "Motivação",
      },
      {
        texto: "Teve algum período sem trabalhar? O que fez nesse tempo?",
        dica: "Lacunas do currículo. Pergunte sobre o profissional — nada de vida pessoal.",
        competencia: "Consistência",
      },
      {
        texto: "Qual trabalho seu te deixa mais orgulhoso? Conte o que você fez ali.",
        dica: "Se a conquista é dele mesmo ou da equipe; se sabe medir resultado.",
        competencia: "Entrega",
      },
    ],
  );

const COMPORTAMENTAL = (): BlocoRoteiro =>
  montarBloco(
    "comportamental",
    "4. Comportamento em situações reais (STAR)",
    "Sair do 'eu sou proativo': cada resposta deve ter situação, o que fez e o resultado.",
    10,
    [
      {
        texto: "Conte uma situação de pressão ou prazo apertado: o que aconteceu, o que você fez e como terminou.",
        dica: "STAR: situação → tarefa → ação → resultado. Se faltar o resultado, pergunte.",
        competencia: "Trabalho sob pressão",
      },
      {
        texto: "Descreva um desentendimento com um colega, chefe ou cliente e como resolveu.",
        dica: "Se assume a parte dele ou coloca a culpa toda no outro.",
        competencia: "Relacionamento",
      },
      {
        texto: "Conte um erro seu no trabalho: como percebeu, o que fez e o que mudou depois.",
        dica: "Quem nunca errou em anos de trabalho não está sendo sincero.",
        competencia: "Responsabilidade",
      },
      {
        texto: "Dê um exemplo de algo que você aprendeu por conta própria porque o trabalho exigiu.",
        dica: "Iniciativa e capacidade de se virar sem alguém mandando.",
        competencia: "Aprendizado",
      },
      {
        texto: "Como você se organiza quando tem várias demandas ao mesmo tempo?",
        dica: "Método concreto (lista, prioridade, combinar prazo) x 'dou um jeito'.",
        competencia: "Organização",
      },
    ],
  );

const FIT = (): BlocoRoteiro =>
  montarBloco(
    "fit",
    "5. Fit com a cultura da SERCAMP",
    "Ver se o jeito de trabalhar combina com o da casa: procedimento, registro e time.",
    5,
    [
      {
        texto: "Em que tipo de ambiente de trabalho você rende melhor?",
        dica: "Compare com a realidade da área (campo, obra, escritório, rotina de cliente).",
        competencia: "Adaptação",
      },
      {
        texto: "O que você espera de um líder?",
        dica: "Se o que ele espera é o que o gestor da vaga entrega.",
        competencia: "Relação com liderança",
      },
      {
        texto:
          "Aqui tudo é feito com procedimento, checklist e registro (ISO 9001). Como você lida com esse tipo de rotina?",
        dica: "Resistência a preencher relatório é um problema real no dia a dia.",
        competencia: "Qualidade e registro",
      },
      {
        texto: "O que é inaceitável para você em um ambiente de trabalho?",
        dica: "Limites e valores; ouça sem julgar e anote o que for incompatível.",
        competencia: "Valores",
      },
    ],
  );

const SEGURANCA = (): BlocoRoteiro =>
  montarBloco(
    "seguranca",
    "6. Segurança do trabalho",
    "Segurança não se negocia: aqui se vê se a pessoa já trabalha com esse cuidado.",
    8,
    [
      {
        texto:
          "Quais treinamentos de segurança você tem (NR-10, NR-35, NR-06, NR-12, NR-33) e quando vencem?",
        dica: "Peça os certificados. Anote a data de validade de cada um.",
        competencia: "Certificações",
      },
      {
        texto: "Como você faz a análise de risco antes de começar um serviço?",
        dica: "APR, permissão de trabalho, checagem de EPI e do local.",
        competencia: "Prevenção",
      },
      {
        texto: "Já precisou interromper um serviço por risco? Conte o que aconteceu.",
        dica: "Direito de recusa — o candidato sabe que pode (e deve) parar?",
        competencia: "Postura de segurança",
      },
      {
        texto: "O cliente pressiona para fazer o serviço energizado, sem desligar. O que você faz?",
        dica: "Situação real na SERCAMP. Resposta certa: não faz, comunica e registra.",
        competencia: "Firmeza técnica",
      },
    ],
  );

const DISPONIBILIDADE = (): BlocoRoteiro =>
  montarBloco(
    "disponibilidade",
    "7. Disponibilidade e logística",
    "Alinhar deslocamento, viagem, escala e prazo para começar — o que costuma derrubar a contratação depois.",
    5,
    [
      {
        texto: "Onde você mora e como viria trabalhar? Quanto tempo leva no trajeto?",
        dica: "Distância grande sem transporte próprio vira falta e atraso.",
        competencia: "Deslocamento",
      },
      {
        texto: "Tem disponibilidade para viagem e pernoite em obra? Por quantos dias seguidos?",
        dica: "Seja explícito sobre a realidade da vaga antes de ouvir o 'sim'.",
        competencia: "Viagem",
      },
      {
        texto: "Tem CNH? Qual categoria e desde quando? Costuma dirigir a serviço?",
        dica: "Confira a validade e se há pontuação que impeça dirigir veículo da empresa.",
        competencia: "Habilitação",
      },
      {
        texto: "Tem disponibilidade para plantão, hora extra ou finais de semana quando o serviço exigir?",
        dica: "Combine agora; depois vira conflito de escala.",
        competencia: "Escala",
      },
      {
        texto: "Está trabalhando? Qual o prazo para começar (aviso prévio)?",
        dica: "Data real de início — é o que o gestor precisa para planejar.",
        competencia: "Disponibilidade de início",
      },
    ],
  );

const REMUNERACAO = (): BlocoRoteiro =>
  montarBloco(
    "remuneracao",
    "8. Remuneração e expectativas",
    "Colocar o valor na mesa antes de investir mais etapas no candidato.",
    5,
    [
      {
        texto: "Qual sua pretensão salarial?",
        dica: "Anote o número no parecer. Se estiver fora da faixa, diga já.",
        competencia: "Pretensão",
      },
      {
        texto: "Qual era sua última remuneração (fixo + variável + benefícios)?",
        dica: "Serve de referência para a proposta e mostra coerência da pretensão.",
        competencia: "Histórico salarial",
      },
      {
        texto: "Está em outro processo seletivo? Em que fase?",
        dica: "Define a urgência da nossa decisão.",
        competencia: "Concorrência",
      },
      {
        texto: "Onde você quer estar profissionalmente daqui a dois anos?",
        dica: "Se o caminho dele cabe dentro da empresa.",
        competencia: "Plano de carreira",
      },
    ],
  );

const ENCERRAMENTO = (): BlocoRoteiro =>
  montarBloco(
    "encerramento",
    "9. Encerramento",
    "Fechar bem: dúvidas do candidato, o que ficou de fora e o combinado do retorno.",
    5,
    [
      {
        texto: "Que dúvidas você tem sobre a vaga, a empresa ou o processo?",
        dica: "As perguntas do candidato dizem muito sobre o interesse dele.",
        competencia: "Interesse",
      },
      {
        texto: "Tem algo importante que não perguntei e que você gostaria de contar?",
        dica: "Costuma trazer o que faltava (restrição, expectativa, experiência esquecida).",
        competencia: "Abertura",
      },
      {
        texto: "Combinado do retorno: qual o próximo passo e até quando damos a resposta?",
        dica: "Diga o prazo e cumpra — é a imagem da empresa com quem não foi contratado.",
        competencia: "Fechamento",
      },
    ],
  );

/* ---------- Bloco técnico: muda conforme o tipo de vaga ---------- */

const TECNICO_GERAL = (): BlocoRoteiro =>
  montarBloco(
    "tecnico",
    "3. Conhecimento da função",
    "Comparar o que a vaga exige com o que a pessoa já fez de fato.",
    10,
    [
      {
        texto: "Das atividades desta vaga, quais você já executou na prática?",
        dica: "Leia a descrição da vaga item a item e vá marcando.",
        competencia: "Domínio técnico",
      },
      {
        texto: "O que você domina hoje e o que precisaria aprender para esta função?",
        dica: "Autoconhecimento e disposição para aprender.",
        competencia: "Autoavaliação",
      },
      {
        texto: "Quais ferramentas, equipamentos ou sistemas você usa no seu trabalho?",
        dica: "Peça exemplos de uso, não a lista.",
        competencia: "Ferramentas",
      },
      {
        texto: "Descreva um trabalho do começo ao fim que represente bem o que você sabe fazer.",
        dica: "Como recebeu a demanda, o que executou e como entregou.",
        competencia: "Execução",
      },
    ],
  );

const TECNICO_OPERACIONAL = (): BlocoRoteiro =>
  montarBloco(
    "tecnico",
    "3. Conhecimento técnico — manutenção elétrica",
    "O núcleo da vaga de campo: o que já executou em instalação, manutenção e ensaio.",
    15,
    [
      {
        texto:
          "Que tipo de serviço elétrico você já executou? (preventiva, corretiva, subestação, transformador, SPDA, ensaios)",
        dica: "Marque o que ele cita sozinho antes de sugerir a lista.",
        competencia: "Escopo técnico",
      },
      {
        texto: "Com que níveis de tensão você trabalha: baixa, média ou alta?",
        dica: "Média/alta exige NR-10 SEP em dia.",
        competencia: "Nível de tensão",
      },
      {
        texto: "Descreva um serviço do início ao fim: como recebeu, o que executou e como entregou o relatório.",
        dica: "O relatório faz parte do serviço aqui — veja se ele trata assim.",
        competencia: "Execução completa",
      },
      {
        texto:
          "Quais instrumentos você domina? (megômetro, alicate amperímetro, terrômetro, TTR, hipot, termovisor)",
        dica: "Peça um exemplo de medição feita e o que o resultado indicou.",
        competencia: "Instrumentação",
      },
      {
        texto: "Já fez manutenção em transformador? (troca/filtragem de óleo, coleta de amostra, análise)",
        dica: "Serviço recorrente da SERCAMP — vale detalhar.",
        competencia: "Transformadores",
      },
      {
        texto: "Sabe ler diagrama unifilar e projeto elétrico? Já executou serviço a partir de projeto?",
        dica: "Se travar aqui, a autonomia em campo é menor.",
        competencia: "Leitura de projeto",
      },
      {
        texto: "Você chega na obra e o serviço não é o que estava previsto. O que faz?",
        dica: "Comunicar antes de improvisar; registrar a mudança de escopo.",
        competencia: "Postura em campo",
      },
      {
        texto: "Como você preenche o relatório/checklist do serviço e registra as fotos?",
        dica: "Aqui o relatório é entregue ao cliente — capricho conta.",
        competencia: "Documentação",
      },
    ],
  );

const TECNICO_ADMINISTRATIVO = (): BlocoRoteiro =>
  montarBloco(
    "tecnico",
    "3. Conhecimento técnico — administrativo",
    "Rotina, sistemas e cuidado com número e prazo.",
    12,
    [
      {
        texto: "Quais sistemas ou ERPs você já usou? Qual seu nível em Excel — dê um exemplo do que faz nele.",
        dica: "'Avançado' sem exemplo não vale. Pergunte de PROCV, tabela dinâmica, filtro.",
        competencia: "Ferramentas",
      },
      {
        texto: "Descreva uma rotina que você organizou ou melhorou no seu trabalho.",
        dica: "Iniciativa e visão de processo.",
        competencia: "Organização de processo",
      },
      {
        texto: "Como você lida com prazos e documentos que não podem atrasar (nota fiscal, ponto, contrato)?",
        dica: "Controle próprio: agenda, lembrete, conferência.",
        competencia: "Controle de prazo",
      },
      {
        texto: "Como você confere o próprio trabalho para não errar em número ou dado?",
        dica: "Um erro de digitação aqui vira erro de pagamento ou de faturamento.",
        competencia: "Atenção a detalhe",
      },
      {
        texto: "Já atendeu cliente ou fornecedor? Como lida com cobrança e reclamação?",
        dica: "Tom de voz e postura ao contar o caso.",
        competencia: "Atendimento",
      },
      {
        texto: "Você lida com informação sigilosa (salário, dado pessoal). Como trata isso?",
        dica: "Discrição é requisito, principalmente em RH/DP e financeiro.",
        competencia: "Sigilo",
      },
    ],
  );

const TECNICO_LIDERANCA = (): BlocoRoteiro =>
  montarBloco(
    "tecnico",
    "3. Liderança e gestão de equipe",
    "Como conduz gente, prazo e resultado — e como responde quando dá errado.",
    15,
    [
      {
        texto: "Quantas pessoas você já liderou e que tipo de equipe era?",
        dica: "Equipe própria x terceiros; campo x escritório.",
        competencia: "Experiência de liderança",
      },
      {
        texto: "Como você distribui o serviço e cobra prazo da equipe?",
        dica: "Método: reunião, quadro, sistema, acompanhamento em campo.",
        competencia: "Gestão do trabalho",
      },
      {
        texto: "Conte como você conduziu alguém com baixo desempenho.",
        dica: "Feedback, plano, prazo — ou só reclamação?",
        competencia: "Desenvolvimento",
      },
      {
        texto: "Já precisou desligar alguém? Como conduziu a conversa?",
        dica: "Firmeza com respeito; se seguiu o RH.",
        competencia: "Decisão difícil",
      },
      {
        texto: "Como você forma quem está começando na função?",
        dica: "Passar conhecimento é parte do cargo aqui.",
        competencia: "Formação de equipe",
      },
      {
        texto: "Que indicadores você acompanhava? O que fazia quando o número saía do esperado?",
        dica: "Gestão por dado, não por impressão.",
        competencia: "Indicadores",
      },
      {
        texto: "Como você garante a segurança da equipe em campo?",
        dica: "Liderança responde pela segurança de quem está com ele.",
        competencia: "Segurança da equipe",
      },
    ],
  );

const TECNICO_COMERCIAL = (): BlocoRoteiro =>
  montarBloco(
    "tecnico",
    "3. Conhecimento técnico — comercial",
    "Prospecção, proposta, negociação e manutenção da carteira.",
    12,
    [
      {
        texto: "Como você prospecta um cliente novo? Descreva do primeiro contato ao agendamento.",
        dica: "Método próprio x esperar o cliente aparecer.",
        competencia: "Prospecção",
      },
      {
        texto: "Conte uma venda difícil que você fechou: do primeiro contato ao pedido.",
        dica: "Ciclo de venda, decisor, objeções superadas.",
        competencia: "Negociação",
      },
      {
        texto: "Como você responde quando o cliente diz que o preço está alto?",
        dica: "Defende valor técnico ou só dá desconto?",
        competencia: "Objeção de preço",
      },
      {
        texto: "Qual meta você tinha e qual foi seu resultado no último ano?",
        dica: "Números concretos; desconfie de 'sempre bati'.",
        competencia: "Resultado",
      },
      {
        texto: "Você monta proposta técnica? Como levanta o escopo com o cliente?",
        dica: "Aqui a proposta é técnica — entender o serviço é parte da venda.",
        competencia: "Proposta técnica",
      },
      {
        texto: "Como você faz o pós-venda e mantém a carteira ativa?",
        dica: "Manutenção elétrica é contrato recorrente; relacionamento é o ativo.",
        competencia: "Carteira",
      },
    ],
  );

const TECNICO_ESTAGIO = (): BlocoRoteiro =>
  montarBloco(
    "tecnico",
    "3. Formação e interesse",
    "Primeiro emprego: o que importa é o curso, o horário e a vontade de aprender.",
    10,
    [
      {
        texto: "O que você está estudando, em que fase está o curso e quando conclui?",
        dica: "Estágio exige matrícula ativa; confira o comprovante.",
        competencia: "Formação",
      },
      {
        texto: "Qual o horário das aulas e como o estágio se encaixaria?",
        dica: "Feche a grade agora — é o motivo mais comum de desistência.",
        competencia: "Compatibilidade de horário",
      },
      {
        texto: "Já fez algum trabalho, projeto ou curso na área? Conte o que fez.",
        dica: "Projeto de faculdade e curso técnico contam.",
        competencia: "Experiência inicial",
      },
      {
        texto: "Conte algo que você aprendeu sozinho recentemente e como aprendeu.",
        dica: "Autonomia de aprendizado vale mais que experiência aqui.",
        competencia: "Aprendizado",
      },
      {
        texto: "O que você espera aprender na SERCAMP?",
        dica: "Expectativa realista sobre a rotina do setor.",
        competencia: "Expectativa",
      },
    ],
  );

/* ------------------------------------------------------------------ *
 *  Os roteiros                                                        *
 * ------------------------------------------------------------------ */

/**
 * Um roteiro por TIPO de vaga. Os blocos de comportamento, cultura,
 * segurança, disponibilidade e remuneração são os mesmos em todos — é o que
 * torna candidatos comparáveis; o que muda é o bloco técnico (3).
 */
export const ROTEIROS: ModeloRoteiro[] = [
  {
    key: "operacional",
    nome: "Operacional / campo (eletricista, técnico, auxiliar)",
    descricao:
      "Vagas de execução em campo e obra: manutenção elétrica, subestação, transformador, SPDA e ensaios.",
    blocos: [
      ABERTURA(),
      TRAJETORIA(),
      TECNICO_OPERACIONAL(),
      COMPORTAMENTAL(),
      FIT(),
      SEGURANCA(),
      DISPONIBILIDADE(),
      REMUNERACAO(),
      ENCERRAMENTO(),
    ],
  },
  {
    key: "administrativo",
    nome: "Administrativo / apoio (RH, DP, financeiro, planejamento)",
    descricao: "Vagas de escritório: rotina, sistema, documento e prazo.",
    blocos: [
      ABERTURA(),
      TRAJETORIA(),
      TECNICO_ADMINISTRATIVO(),
      COMPORTAMENTAL(),
      FIT(),
      SEGURANCA(),
      DISPONIBILIDADE(),
      REMUNERACAO(),
      ENCERRAMENTO(),
    ],
  },
  {
    key: "lideranca",
    nome: "Liderança (encarregado, supervisor, coordenador, gerente)",
    descricao: "Vagas que respondem por equipe, prazo, cliente e segurança de quem está em campo.",
    blocos: [
      ABERTURA(),
      TRAJETORIA(),
      TECNICO_LIDERANCA(),
      COMPORTAMENTAL(),
      FIT(),
      SEGURANCA(),
      DISPONIBILIDADE(),
      REMUNERACAO(),
      ENCERRAMENTO(),
    ],
  },
  {
    key: "comercial",
    nome: "Comercial (vendedor, consultor, SDR)",
    descricao: "Vagas de prospecção, proposta e carteira de clientes.",
    blocos: [
      ABERTURA(),
      TRAJETORIA(),
      TECNICO_COMERCIAL(),
      COMPORTAMENTAL(),
      FIT(),
      SEGURANCA(),
      DISPONIBILIDADE(),
      REMUNERACAO(),
      ENCERRAMENTO(),
    ],
  },
  {
    key: "estagio",
    nome: "Estágio / aprendiz / primeiro emprego",
    descricao: "Sem experiência anterior: formação, horário de aula e vontade de aprender.",
    blocos: [
      ABERTURA(),
      TRAJETORIA(),
      TECNICO_ESTAGIO(),
      COMPORTAMENTAL(),
      FIT(),
      SEGURANCA(),
      DISPONIBILIDADE(),
      REMUNERACAO(),
      ENCERRAMENTO(),
    ],
  },
  {
    key: "geral",
    nome: "Geral (serve para qualquer vaga)",
    descricao: "Quando a vaga não se encaixa nos demais — o bloco técnico é aberto.",
    blocos: [
      ABERTURA(),
      TRAJETORIA(),
      TECNICO_GERAL(),
      COMPORTAMENTAL(),
      FIT(),
      SEGURANCA(),
      DISPONIBILIDADE(),
      REMUNERACAO(),
      ENCERRAMENTO(),
    ],
  },
];

export const ROTEIRO_PADRAO = "geral";

export function roteiroPorKey(key: string | null | undefined): ModeloRoteiro {
  return ROTEIROS.find((r) => r.key === key) ?? ROTEIROS.find((r) => r.key === ROTEIRO_PADRAO)!;
}

/**
 * Adivinha o roteiro pelo CARGO da entrevista — abre a tela já no roteiro
 * certo, e o RH troca se quiser. Sem acento e sem caixa, porque o cargo é
 * digitado à mão.
 */
export function sugerirRoteiro(cargo: string | null | undefined): string {
  const c = String(cargo ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
  if (!c.trim()) return ROTEIRO_PADRAO;

  if (/(estagi|aprendiz|trainee|jovem aprendiz)/.test(c)) return "estagio";
  if (/(gerente|coordenad|supervisor|encarregad|lider|chefe|diretor)/.test(c)) return "lideranca";
  if (/(vend|comercial|consultor|sdr|represent|account)/.test(c)) return "comercial";
  if (
    /(eletric|tecnic|mecanic|montador|instalador|operador|auxiliar de (obra|campo|manuten)|manutenc|soldad|encanador|ajudante|motorista|almoxarif|laborator|engenheir)/.test(
      c,
    )
  ) {
    return "operacional";
  }
  if (
    /(administrat|assistente|analista|auxiliar|financ|rh|recursos humanos|departamento pessoal|compras|fiscal|contab|recepc|secretar|qualidade|planejamento)/.test(
      c,
    )
  ) {
    return "administrativo";
  }
  return ROTEIRO_PADRAO;
}

/* ------------------------------------------------------------------ *
 *  O roteiro PREENCHIDO (o que a tela edita e o banco guarda)          *
 * ------------------------------------------------------------------ */

export interface PerguntaPreenchida extends PerguntaRoteiro {
  /** O que o candidato respondeu, na palavra do entrevistador. */
  resposta: string;
  /** Nota de 1 a 5 (ESCALA_ROTEIRO); "" = ainda não avaliada. */
  nota: string;
}

export interface BlocoPreenchido extends Omit<BlocoRoteiro, "perguntas"> {
  perguntas: PerguntaPreenchida[];
}

/** Blocos em branco de um modelo — o ponto de partida da tela. */
export function blocosEmBranco(key: string): BlocoPreenchido[] {
  return roteiroPorKey(key).blocos.map((b) => ({
    ...b,
    perguntas: b.perguntas.map((p) => ({ ...p, resposta: "", nota: "" })),
  }));
}

/** Média das notas dadas (0 preenchida = null). É a nota geral do candidato. */
export function mediaRoteiro(blocos: BlocoPreenchido[]): number | null {
  const notas = blocos
    .flatMap((b) => b.perguntas)
    .map((p) => Number(p.nota))
    .filter((n) => Number.isFinite(n) && n >= 1 && n <= 5);
  if (notas.length === 0) return null;
  return Math.round((notas.reduce((a, b) => a + b, 0) / notas.length) * 10) / 10;
}

/** Quanto do roteiro já foi percorrido (anotado ou avaliado). */
export function progressoRoteiro(blocos: BlocoPreenchido[]): { feitas: number; total: number; pct: number } {
  const todas = blocos.flatMap((b) => b.perguntas);
  const feitas = todas.filter((p) => p.resposta.trim() !== "" || String(p.nota).trim() !== "").length;
  return {
    feitas,
    total: todas.length,
    pct: todas.length ? Math.round((feitas / todas.length) * 100) : 0,
  };
}

/** Tempo somado dos blocos — quanto a entrevista deve durar. */
export function duracaoSugerida(blocos: BlocoPreenchido[] | BlocoRoteiro[]): number {
  return blocos.reduce((soma, b) => soma + (Number(b.minutos) || 0), 0);
}

/** Status da entrevista que corresponde à recomendação do parecer. */
export function statusDaRecomendacao(recomendacao: string): string | null {
  return RECOMENDACOES.find((r) => r.valor === recomendacao)?.status ?? null;
}
